import { CompareFilterDefinitionInfo } from '@modules/analysis/components/AnalysisDefinitionInfo';
import { WhereClausePreview } from '@modules/common/components/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import {
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { CircleRounded } from '@mui/icons-material';
import { alpha, Stack, Typography } from '@mui/material';
import { AnalysisDefinitionInfoItem } from './AnalysisDefinitionInfoItem';

interface QuantifyFilterDefinitionInfoProps {
  shouldHideTooltips?: boolean;
}

export const QuantifyFilterDefinitionInfo = ({
  shouldHideTooltips = false,
}: QuantifyFilterDefinitionInfoProps) => {
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const whereClause = useQuantifyStore(
    ({ query }) => query.whereClauseConfig?.whereClause,
  );
  const compareFilterAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldAName ||
      DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldBName ||
      DEFAULT_COMPARE_FILTER_B_NAME,
  );

  const { colorA, colorB } = useGetCompareColors();

  if (compareTo?.type === 'whereClause') {
    return (
      <>
        <AnalysisDefinitionInfoItem
          leftNode={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CircleRounded sx={{ color: colorA, width: 8 }} />
              <Typography variant="caption">{compareFilterAName}:</Typography>
            </Stack>
          }
          rightNode={
            <CompareFilterDefinitionInfo
              shouldHideTooltips={shouldHideTooltips}
              compareField="A"
            />
          }
        />
        <AnalysisDefinitionInfoItem
          leftNode={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CircleRounded sx={{ color: colorB, width: 8 }} />
              <Typography variant="caption">{compareFilterBName}:</Typography>
            </Stack>
          }
          rightNode={
            <CompareFilterDefinitionInfo
              shouldHideTooltips={shouldHideTooltips}
              compareField="B"
            />
          }
        />
      </>
    );
  }

  const whereClausePreviewNode = (
    <WhereClausePreview
      shouldHideTooltips={shouldHideTooltips}
      whereClause={whereClause}
      sxProps={{
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
      }}
    />
  );

  const isWhereClauseEmpty = !whereClause || isEmptyWhereClause(whereClause);

  return (
    <AnalysisDefinitionInfoItem
      leftNode="Filter by:"
      rightNode={
        !isWhereClauseEmpty ? whereClausePreviewNode : 'No filters applied'
      }
    />
  );
};
